import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, X, Car } from "lucide-react";

const districts = [
  "Bagalkot",
  "Bangalore Rural",
  "Bangalore Urban",
  "Belagavi",
  "Ballari",
  "Bidar",
  "Chamarajanagar",
  "Chikkaballapur",
  "Chikkamagaluru",
  "Chitradurga",
  "Dakshina Kannada",
  "Davanagere",
  "Dharwad",
  "Gadag",
  "Hassan",
  "Haveri",
  "Kalaburagi",
  "Kodagu",
  "Kolar",
  "Koppal",
  "Mandya",
  "Mysuru",
  "Raichur",
  "Ramanagara",
  "Shivamogga",
  "Tumakuru",
  "Udupi",
  "Uttara Kannada",
  "Vijayapura",
  "Yadgir",
];

export type VehicleRegistration = {
  registration_type: string;
  mt: string;
  id: string;
  date: string;
  registration_number: string;
  owner_name: string;
  mobile: string;
  alternate_mobile?: string;
  district: string;
  imei: string;
  sim_number1: string;
  sim_number2: string;
  sim_start: string;
  sim_end: string;
  rc_copy_path: string;
  id_proof_path: string;
  status: string;

  // File objects (new uploads)
  rc_copy?: File | null;
  id_proof?: File | null;
};

interface EditRegistrationModalProps {
  editData: VehicleRegistration | null;
  setEditData: (data: VehicleRegistration | null) => void;
  formSections: Array<{
    title: string;
    icon: React.ReactNode;
    fields: Array<{ label: string; key: string; type: string }>;
  }>;
  userRole: string;
  onEditSuccess?: () => void;
}

const EditRegistrationModal: React.FC<EditRegistrationModalProps> = ({
  editData,
  setEditData,
  formSections,
  userRole,
  onEditSuccess,
}) => {
  if (!editData) return null;

  const handleFileChange = (
    field: "rc_copy" | "id_proof",
    file: File | null
  ) => {
    setEditData({ ...editData, [field]: file });
  };

  const handleViewFile = async (type, filename) => {
    const url = `${
      import.meta.env.VITE_API_URL
    }/api/vehicle/file/${type}/${filename}`;
    const response = await fetch(url, { credentials: "include" });
    if (response.ok) {
      const blob = await response.blob();
      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, "_blank");
    } else {
      toast({
        title: "Error",
        description: "Unauthorized or file not found",
        variant: "destructive",
      });
    }
  };

  // Helper for validation
  const getMissingFields = () => {
    const missing = [];
    // Always required
    if (!editData.registration_type) missing.push("Registration Type");
    if (!editData.mt) missing.push("MT (Metric Ton)");
    if (!editData.district) missing.push("District");
    if (!editData.registration_number)
      missing.push("Vehicle Registration Number");
    if (!editData.owner_name) missing.push("Vehicle Owner Name");
    // Owner Mobile: exactly 10 digits
    if (!editData.mobile) {
      missing.push("Vehicle Owner Mobile Number");
    } else if (!/^\d{10}$/.test(editData.mobile)) {
      missing.push("Owner Mobile must be exactly 10 digits");
    }
    // Alternate Mobile: only 10 digits, optional for both
    if (editData.alternate_mobile) {
      if (!/^\d{10}$/.test(editData.alternate_mobile)) {
        missing.push("Alternate Number must be exactly 10 digits");
      }
    }
    // File checks
    if (!editData.rc_copy_path && !editData.rc_copy)
      missing.push("Vehicle RC Copy");
    if (!editData.id_proof_path && !editData.id_proof)
      missing.push("Owner ID Proof");
    // For admin, IMEI, SIM, SIM validity required
    if (userRole === "admin") {
      if (!editData.imei) {
        missing.push("Device IMEI Number");
      } else if (!/^\d{15}$/.test(editData.imei)) {
        missing.push("IMEI must be exactly 15 digits");
      }
      if (!editData.sim_number1) {
        missing.push("Device SIM Number Airtel");
      } else if (!/^\d{13}$/.test(editData.sim_number1)) {
        missing.push("SIM Number Airtel must be exactly 13 digits");
      }
      if (!editData.sim_number2) {
        missing.push("Device SIM Number BSNL");
      } else if (!/^\d{13}$/.test(editData.sim_number2)) {
        missing.push("SIM Number BSNL must be exactly 13 digits");
      }
      if (!editData.sim_start) missing.push("SIM Validity Start Date");
      if (!editData.sim_end) missing.push("SIM Validity End Date");
    }
    // For staff, IMEI, SIM, SIM validity are optional
    return missing;
  };

  // Status logic
  const isDeviceFieldsFilled =
    editData.imei &&
    editData.sim_number1 &&
    editData.sim_number2 &&
    editData.sim_start &&
    editData.sim_end;
  // For admin, use selected status if changed; for staff, use device fields logic
  const status =
    userRole === "admin"
      ? editData.status || (isDeviceFieldsFilled ? "Active" : "Pending")
      : isDeviceFieldsFilled
      ? "Active"
      : "Pending";

  const handleSave = async () => {
    const missingFields = getMissingFields();

    // For non-admin roles, validate IMEI and SIM fields only if user entered any value
    if (userRole !== "admin") {
      if (
        editData.imei &&
        editData.imei.length > 0 &&
        editData.imei.length < 15
      ) {
        toast({
          title: "IMEI Format Error",
          description: "IMEI number must be exactly 15 digits.",
          variant: "destructive",
        });
        return;
      }
      if (
        editData.sim_number1 &&
        editData.sim_number1.length > 0 &&
        editData.sim_number1.length < 13
      ) {
        toast({
          title: "SIM Format Error",
          description: "SIM number (Airtel) must be exactly 13 digits.",
          variant: "destructive",
        });
        return;
      }
      if (
        editData.sim_number2 &&
        editData.sim_number2.length > 0 &&
        editData.sim_number2.length < 13
      ) {
        toast({
          title: "SIM Format Error",
          description: "SIM number (BSNL) must be exactly 13 digits.",
          variant: "destructive",
        });
        return;
      }
    }

    if (missingFields.length > 0) {
      toast({
        title: "Please fill all mandatory fields",
        description: `Missing: ${missingFields.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    // Append files or paths
    if (editData.rc_copy) {
      formData.append("rc_copy", editData.rc_copy);
    } else {
      formData.append("rc_copy_path", editData.rc_copy_path);
    }
    if (editData.id_proof) {
      formData.append("id_proof", editData.id_proof);
    } else {
      formData.append("id_proof_path", editData.id_proof_path);
    }
    // Append all other fields except file objects
    Object.entries(editData).forEach(([key, value]) => {
      if (key !== "rc_copy" && key !== "id_proof") {
        formData.append(key, value as string);
      }
    });
    formData.set("status", status);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/vehicle/update`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );
      const result = await response.json();
      if (response.ok) {
        toast({
          title: "Success",
          description: result.message || "Vehicle registered successfully.",
        });
        setEditData(null);
        if (onEditSuccess) onEditSuccess();
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to update vehicle registration.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("Error saving changes:", err);
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="max-w-5xl p-0 h-[90vh] flex flex-col bg-background">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-dialog-header border-b border-border px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-light rounded-lg">
                <Car className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  Edit Vehicle Registration
                </h2>
                <p className="text-sm text-muted-foreground">
                  Update vehicle and owner information
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEditData(null)}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {formSections.map((section, sectionIndex) => (
              <Card key={sectionIndex} className="shadow-sm border-2">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary-light rounded-lg">
                      {section.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {section.title}
                    </h3>
                  </div>
                  <div className="space-y-6">
                    {section.fields.map((field) => {
                      // Determine if field is mandatory for current userRole
                      let isMandatory = false;
                      if (
                        [
                          "registration_type",
                          "mt",
                          "district",
                          "registration_number",
                          "owner_name",
                          "mobile",
                        ].includes(field.key)
                      ) {
                        isMandatory = true;
                      }
                      if (
                        userRole === "admin" &&
                        [
                          "imei",
                          "sim_number1",
                          "sim_number2",
                          "sim_start",
                          "sim_end",
                        ].includes(field.key)
                      ) {
                        isMandatory = true;
                      }
                      // Render label with red * if mandatory
                      const labelContent = (
                        <span>
                          {field.label}
                          {isMandatory && (
                            <span style={{ color: "red", marginLeft: 4 }}>
                              *
                            </span>
                          )}
                        </span>
                      );
                      if (field.key === "registration_type") {
                        return (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={field.key}>{labelContent}</Label>
                            <Select
                              value={editData.registration_type || ""}
                              onValueChange={(value) =>
                                setEditData({
                                  ...editData,
                                  registration_type: value,
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select registration type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="New Registration">
                                  New Registration
                                </SelectItem>
                                <SelectItem value="Device Replacement">
                                  Device Replacement
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      }

                      if (field.key === "mt") {
                        return (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={field.key}>{labelContent}</Label>
                            <Select
                              value={editData.mt || ""}
                              onValueChange={(value) =>
                                setEditData({ ...editData, mt: value })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select MT" />
                              </SelectTrigger>
                              <SelectContent>
                                {[2, 3, 6, 8, 10, 12, 20, 25, 30].map((mt) => (
                                  <SelectItem key={mt} value={mt.toString()}>
                                    {mt}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      }

                      if (field.key === "district") {
                        return (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={field.key}>{labelContent}</Label>
                            <Select
                              value={editData.district || ""}
                              onValueChange={(value) =>
                                setEditData({ ...editData, district: value })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select district" />
                              </SelectTrigger>
                              <SelectContent>
                                {districts.map((district) => (
                                  <SelectItem key={district} value={district}>
                                    {district}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      }

                      if (
                        section.title === "Status & Documents" &&
                        userRole === "admin"
                      ) {
                        return (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={field.key}>Status</Label>
                            <Select
                              value={editData.status || ""}
                              onValueChange={(value) =>
                                setEditData({ ...editData, status: value })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="Inactive">
                                  Inactive
                                </SelectItem>
                                <SelectItem value="Pending">Pending</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      }

                      // Custom input restrictions
                      if (field.key === "owner_name") {
                        return (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={field.key}>{labelContent}</Label>
                            <Input
                              id={field.key}
                              type="text"
                              pattern="^[A-Za-z ]*$"
                              inputMode="text"
                              value={editData.owner_name || ""}
                              onChange={(e) => {
                                // Only allow letters and spaces
                                const val = e.target.value.replace(
                                  /[^A-Za-z ]/g,
                                  ""
                                );
                                setEditData({ ...editData, owner_name: val });
                              }}
                              className="transition-all duration-200 hover:border-primary-dark"
                            />
                          </div>
                        );
                      }
                      if (
                        field.key === "mobile" ||
                        field.key === "alternate_mobile"
                      ) {
                        return (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={field.key}>{labelContent}</Label>
                            <Input
                              id={field.key}
                              type="text"
                              inputMode="numeric"
                              pattern="^\\d{0,10}$"
                              maxLength={10}
                              value={editData[field.key] || ""}
                              onChange={(e) => {
                                // Only allow digits, max 10
                                let val = e.target.value.replace(/[^0-9]/g, "");
                                if (val.length > 10) val = val.slice(0, 10);
                                setEditData({ ...editData, [field.key]: val });
                              }}
                              className="transition-all duration-200 hover:border-primary-dark"
                            />
                          </div>
                        );
                      }
                      if (field.key === "imei") {
                        return (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={field.key}>{labelContent}</Label>
                            <Input
                              id={field.key}
                              type="text"
                              inputMode="numeric"
                              maxLength={15}
                              value={editData.imei || ""}
                              onChange={(e) => {
                                // Only allow digits, max 15
                                let val = e.target.value.replace(/[^0-9]/g, "");
                                if (val.length > 15) val = val.slice(0, 15);
                                setEditData({ ...editData, imei: val });
                              }}
                              className="transition-all duration-200 hover:border-primary-dark"
                            />
                            {editData.imei &&
                              editData.imei.length > 0 &&
                              editData.imei.length < 15 && (
                                <div
                                  style={{ color: "red", fontSize: "0.95em" }}
                                >
                                  IMEI number must be exactly 15 digits.
                                </div>
                              )}
                          </div>
                        );
                      }
                      if (field.key === "sim_number1") {
                        return (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={field.key}>{labelContent}</Label>
                            <Input
                              id={field.key}
                              type="text"
                              inputMode="numeric"
                              maxLength={13}
                              value={editData.sim_number1 || ""}
                              onChange={(e) => {
                                // Only allow digits, max 13
                                let val = e.target.value.replace(/[^0-9]/g, "");
                                if (val.length > 13) val = val.slice(0, 13);
                                setEditData({ ...editData, sim_number1: val });
                              }}
                              className="transition-all duration-200 hover:border-primary-dark"
                            />
                            {editData.sim_number1 &&
                              editData.sim_number1.length > 0 &&
                              editData.sim_number1.length < 13 && (
                                <div
                                  style={{ color: "red", fontSize: "0.95em" }}
                                >
                                  SIM number (Airtel) must be exactly 13 digits.
                                </div>
                              )}
                          </div>
                        );
                      }
                      if (field.key === "sim_number2") {
                        return (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={field.key}>{labelContent}</Label>
                            <Input
                              id={field.key}
                              type="text"
                              inputMode="numeric"
                              maxLength={13}
                              value={editData.sim_number2 || ""}
                              onChange={(e) => {
                                // Only allow digits, max 13
                                let val = e.target.value.replace(/[^0-9]/g, "");
                                if (val.length > 13) val = val.slice(0, 13);
                                setEditData({ ...editData, sim_number2: val });
                              }}
                              className="transition-all duration-200 hover:border-primary-dark"
                            />
                            {editData.sim_number2 &&
                              editData.sim_number2.length > 0 &&
                              editData.sim_number2.length < 13 && (
                                <div
                                  style={{ color: "red", fontSize: "0.95em" }}
                                >
                                  SIM number (BSNL) must be exactly 13 digits.
                                </div>
                              )}
                          </div>
                        );
                      }
                      // Default input
                      return (
                        <div key={field.key} className="space-y-2">
                          <Label htmlFor={field.key}>{labelContent}</Label>
                          <Input
                            id={field.key}
                            type={field.type}
                            value={(() => {
                              if (field.type === "date") {
                                const val =
                                  editData[
                                    field.key as keyof VehicleRegistration
                                  ];
                                if (!val) return "";
                                if (
                                  typeof val === "string" ||
                                  typeof val === "number" ||
                                  val instanceof Date
                                ) {
                                  const d = new Date(val);
                                  if (isNaN(d.getTime())) return "";
                                  return d.toISOString().slice(0, 10);
                                }
                                return "";
                              }
                              const value =
                                editData[
                                  field.key as keyof VehicleRegistration
                                ];
                              if (
                                typeof value === "string" ||
                                typeof value === "number"
                              ) {
                                return value;
                              }
                              return "";
                            })()}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                [field.key]: e.target.value,
                              })
                            }
                            className="transition-all duration-200 hover:border-primary-dark"
                          />
                        </div>
                      );
                    })}

                    {section.title === "Status & Documents" && (
                      <>
                        {/* RC Copy Upload */}
                        <div className="space-y-2 md:col-span-2">
                          <Label>
                            RC Copy (JPG, PNG, PDF)
                            <span style={{ color: "red", marginLeft: 4 }}>
                              *
                            </span>
                            <span
                              style={{
                                color: "#888",
                                marginLeft: 4,
                                fontSize: "0.95em",
                              }}
                            >
                              less than 1MB
                            </span>
                          </Label>
                          <div className="flex items-center gap-4">
                            <Input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={(e) =>
                                handleFileChange(
                                  "rc_copy",
                                  e.target.files?.[0] || null
                                )
                              }
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                            {editData.rc_copy_path && (
                              <Button
                                onClick={() =>
                                  handleViewFile(
                                    "rc_copies",
                                    editData.rc_copy_path.replace(
                                      /^.*[\\/]/,
                                      ""
                                    )
                                  )
                                }
                              >
                                View current file
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* ID Proof Upload */}
                        <div className="space-y-2 md:col-span-2">
                          <Label>
                            ID Proof (JPG, PNG, PDF)
                            <span style={{ color: "red", marginLeft: 4 }}>
                              *
                            </span>
                            <span
                              style={{
                                color: "#888",
                                marginLeft: 4,
                                fontSize: "0.95em",
                              }}
                            >
                              less than 1MB
                            </span>
                          </Label>
                          <div className="flex items-center gap-4">
                            <Input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={(e) =>
                                handleFileChange(
                                  "id_proof",
                                  e.target.files?.[0] || null
                                )
                              }
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                            {editData.id_proof_path && (
                              <Button
                                onClick={() =>
                                  handleViewFile(
                                    "id_proofs",
                                    editData.id_proof_path.replace(
                                      /^.*[\\/]/,
                                      ""
                                    )
                                  )
                                }
                              >
                                View current file
                              </Button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto bg-dialog-footer border-t border-border px-8 py-4">
          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => setEditData(null)}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="min-w-[120px] bg-primary hover:bg-primary-dark"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRegistrationModal;
